import { Crown, Trophy, Users } from 'lucide-react'

import { TIER_ACCENT } from '@/lib/squadron/tierMeta'

/**
 * Stylised, static preview of what e404 looks like — sits to the right of
 * the hero copy on lg+. The "what is this product?" first-impression
 * problem doesn't get solved by copy alone; this is the visual that
 * turns "I don't know what they sell" into "ah — squadrons + events".
 *
 * Deliberately static (no live data, no motion) per Emil's high-frequency
 * surface rule: the landing hero is the most-viewed thing on the site,
 * so any decorative motion would burn quickly. Visual interest comes
 * from layered cards, tactical brackets, and the tier-bronze accent on
 * the squadron header — same visual language used inside the real app
 * so the preview feels honest, not aspirational mockup-fiction.
 */
export function HeroPreview() {
  const accent = TIER_ACCENT.LEVEL_1 // bronze — first paid step; reads as "real squad"

  return (
    <div
      className="relative isolate w-full max-w-md"
      role="img"
      aria-label="Preview of e404 — a squadron dashboard showing a Bronze-tier squadron with 23 of 25 members, upcoming tournament, and three squadmates online"
    >
      {/* Halo behind the device frame */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-8 -z-10 rounded-[3rem] opacity-40 blur-3xl"
        style={{ background: 'var(--gradient-accent)' }}
      />

      {/* Frame */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-surface/80 backdrop-blur-xl">
        {/* Top status strip — mimics the in-app TopBar so it reads as a
            real surface, not a decorative tile. */}
        <div className="flex items-center justify-between border-b border-border/60 bg-bg/60 px-4 py-2">
          <span className="font-display flex items-center gap-2 text-[9px] tracking-[0.25em] text-fg-subtle uppercase">
            <span className="size-1.5 rounded-full bg-success" aria-hidden />
            All systems nominal
          </span>
          <span className="font-display text-[9px] tracking-[0.25em] text-fg-subtle uppercase">
            e404 · ops
          </span>
        </div>

        <div className="flex flex-col gap-3 p-4">
          {/* Squadron header card — tier-bronze identity */}
          <div
            className="relative overflow-hidden rounded-xl border bg-surface/60 p-3.5"
            style={{ borderColor: `${accent}66` }}
          >
            <span
              aria-hidden
              className="absolute inset-x-0 top-0 h-[2px]"
              style={{
                background: `linear-gradient(90deg, ${accent}, ${accent}66 70%, transparent)`,
              }}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage: `radial-gradient(circle at top right, ${accent}1F, transparent 55%)`,
              }}
            />
            <div className="relative flex items-start gap-3">
              {/* Squadron logo — tactical tile with shortTag */}
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border"
                style={{
                  backgroundColor: `${accent}26`,
                  borderColor: `${accent}99`,
                }}
              >
                <span
                  className="font-display text-sm font-black"
                  style={{ color: accent }}
                >
                  ALP
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-display truncate text-sm font-bold text-fg">
                  Alpha Squadron
                </p>
                <p className="font-display mt-0.5 flex flex-wrap items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase">
                  <span style={{ color: accent }}>[ALPHA]</span>
                  {/* Mini TierBadge — same visual language as in-app */}
                  <span
                    className="font-display inline-flex items-center gap-1 rounded border px-1.5 py-px text-[9px] font-bold"
                    style={{
                      color: accent,
                      borderColor: `${accent}99`,
                      backgroundColor: `${accent}1F`,
                    }}
                  >
                    <Crown className="size-2.5" aria-hidden />
                    BRONZE
                  </span>
                </p>
              </div>
              {/* Member count */}
              <div className="flex flex-col items-end gap-0.5">
                <p
                  className="font-display text-xl leading-none font-black tabular-nums"
                  style={{ color: accent }}
                >
                  23
                </p>
                <p className="font-display text-[9px] tracking-[0.18em] text-fg-subtle uppercase">
                  of 25
                </p>
              </div>
            </div>
          </div>

          {/* KPI mini-row — two stat tiles */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-border bg-surface/60 px-3 py-2">
              <p className="font-display text-[9px] tracking-[0.2em] text-fg-subtle uppercase">
                Online now
              </p>
              <p className="font-display mt-0.5 flex items-center gap-1.5 text-base font-black text-success tabular-nums">
                <span className="size-1.5 rounded-full bg-success" aria-hidden />
                3
              </p>
            </div>
            <div className="rounded-lg border border-border bg-surface/60 px-3 py-2">
              <p className="font-display text-[9px] tracking-[0.2em] text-fg-subtle uppercase">
                Events this week
              </p>
              <p className="font-display mt-0.5 text-base font-black text-fg tabular-nums">
                4
              </p>
            </div>
          </div>

          {/* Featured event chip — tournament tonight */}
          <div className="flex items-center gap-3 rounded-lg border border-warning/30 bg-warning/[0.06] px-3 py-2">
            <span
              aria-hidden
              className="flex size-7 shrink-0 items-center justify-center rounded-md bg-warning/15 text-warning"
            >
              <Trophy className="size-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-display truncate text-[11px] font-bold text-fg">
                CoH3 Friday Bracket
              </p>
              <p className="font-display text-[9px] tracking-[0.15em] text-fg-subtle uppercase">
                Tonight · 20:00 · 12 / 16 in
              </p>
            </div>
            <span className="font-display rounded border border-warning/30 px-1.5 py-0.5 text-[8px] font-bold tracking-[0.15em] text-warning uppercase">
              SE
            </span>
          </div>

          {/* Roster strip — avatar stack + count */}
          <div className="flex items-center justify-between rounded-lg border border-border bg-surface/60 px-3 py-2">
            <span className="font-display flex items-center gap-2 text-[10px] tracking-[0.18em] text-fg-subtle uppercase">
              <Users className="size-3" aria-hidden />
              Roster
            </span>
            <div className="flex items-center -space-x-1.5">
              {['NL', 'KR', 'SR', 'JM', 'AT'].map((initials, i) => (
                <span
                  key={initials}
                  className="font-display flex size-5 items-center justify-center rounded-full border border-bg bg-surface text-[8px] font-bold text-fg-muted"
                  style={{
                    // Slightly stagger z-order so the stack reads naturally
                    zIndex: 5 - i,
                  }}
                  aria-hidden
                >
                  {initials}
                </span>
              ))}
              <span className="font-display flex size-5 items-center justify-center rounded-full border border-bg bg-primary/15 text-[8px] font-bold text-primary">
                +18
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Corner brackets — same tactical detail as the page-level hero */}
      <PreviewBracket position="tl" />
      <PreviewBracket position="tr" />
      <PreviewBracket position="bl" />
      <PreviewBracket position="br" />
    </div>
  )
}

function PreviewBracket({ position }: { position: 'tl' | 'tr' | 'bl' | 'br' }) {
  const positions = {
    tl: '-top-1.5 -left-1.5 border-t border-l',
    tr: '-top-1.5 -right-1.5 border-t border-r',
    bl: '-bottom-1.5 -left-1.5 border-b border-l',
    br: '-bottom-1.5 -right-1.5 border-b border-r',
  } as const

  return (
    <span
      aria-hidden
      className={`pointer-events-none absolute size-3 border-primary/40 ${positions[position]}`}
    />
  )
}
